import React, { useMemo } from 'react';
import { useASINSalesHistory } from '@/context/ASINSalesHistoryContext';
import { Card, CardContent } from '@/components/ui/card';
import { FaChartLine } from 'react-icons/fa';
import { ASINViewerChart } from '@/components/asin-sales-history/ASINViewerChart';
import { ASINViewerTable } from '@/components/asin-sales-history/ASINViewerTable';

const ASINViewer: React.FC = () => {
  const {
    selectedCompany,
    data: rawData,
    loading,
    error,
    selectedFilter,
    isLoadingAsins
  } = useASINSalesHistory();

  const selectedASIN = selectedFilter?.asin;

  // Filter data for the selected ASIN
  const filteredData = useMemo(() => {
    if (!rawData.length || !selectedASIN || selectedASIN === 'all') return [];
    
    const filtered = rawData.filter(row => row.asin === selectedASIN);
    // Sort by date descending (most recent first) for display
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [rawData, selectedASIN]);

  // If no company selected, show company selection message
  if (!selectedCompany) {
    return (
      <div className="h-full flex items-center justify-center p-4 md:p-6 bg-gray-50 dark:bg-gray-900">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 md:py-12 px-4">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
              <FaChartLine className="h-12 w-12 md:h-16 md:w-16 text-gray-300 dark:text-gray-600" />
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold text-gray-400 dark:text-gray-500">
                  ASIN Viewer
                </h3>
                <p className="text-xs md:text-sm text-gray-400 dark:text-gray-500 px-2">
                  Please select a company from the menu on the left.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If no ASIN selected or "all" is selected, show greyed out message
  if (!selectedASIN || selectedASIN === 'all') {
    return (
      <div className="h-full flex items-center justify-center p-4 md:p-6 bg-gray-50 dark:bg-gray-900">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 md:py-12 px-4">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4 opacity-50">
              <FaChartLine className="h-12 w-12 md:h-16 md:w-16 text-gray-400 dark:text-gray-600" />
              <div className="space-y-2">
                <h3 className="text-lg md:text-xl font-semibold text-gray-600 dark:text-gray-400">
                  ASIN Viewer
                </h3>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 px-2">
                  Please enter an ASIN in the menu on the left.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if there's an error
  if (error) {
    return (
      <div className="p-4 md:p-6">
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="py-8 md:py-12 px-4">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
              <div className="space-y-2">
                <h3 className="text-base md:text-lg font-semibold text-red-900 dark:text-red-100">
                  Error Loading Data
                </h3>
                <p className="text-xs md:text-sm text-red-700 dark:text-red-300 max-w-md px-2">
                  {error}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while fetching data
  if (loading || isLoadingAsins) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="py-8 md:py-12 px-4">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-violet-600"></div>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 px-2">
                Loading data for {selectedASIN || 'ASIN'}...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If ASIN is selected but no data found (after loading is complete)
  if (filteredData.length === 0 && !loading && !isLoadingAsins) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="py-8 md:py-12 px-4">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
              <FaChartLine className="h-10 w-10 md:h-12 md:w-12 text-gray-400 dark:text-gray-600" />
              <div className="space-y-2">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
                  No data found for this ASIN
                </h3>
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 max-w-md px-2 break-all">
                  ASIN: {selectedASIN}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show chart and table when data is available
  return (
    <div className="h-full overflow-auto">
      <div className="p-4 md:p-6 space-y-4 md:space-y-6 max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              ASIN Viewer
            </h1>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              {selectedASIN} ({filteredData.length} records)
            </p>
          </div>
        </div>

        {/* Chart - Above the table */}
        <div className="w-full overflow-hidden">
          <ASINViewerChart data={filteredData} />
        </div>

        {/* Data Table - Below the chart */}
        <div className="w-full overflow-hidden">
          <ASINViewerTable data={filteredData} />
        </div>
      </div>
    </div>
  );
};

export default ASINViewer;